<template>
  <div class="areaColumn chart__container">
    <h5 class="f-title-7">{{ scenario }}</h5>
    <StagedChart
      :stages="stages"
      :stage="scenarios.indexOf(scenario)"
      :baseOptions="baseOptions"
      :updateArgs="[true, false, true]"
    />
    <slot name="legend" />
  </div>
</template>

<script>
import StagedChart from "./StagedChart";
export default {
  props: {
    scenario: {
      type: String,
      required: true,
    },
    /* Series array keyed by scenario like: {APS: [...], STEPS: [...]}*/
    chartData: {
      type: Object,
      required: true,
    },
    units: {
      type: String,
      default: " EJ",
    },
    showTotals: {
      type: Boolean,
      default: false,
    },
    showValues: {
      type: Boolean,
      default: false,
    },
    showValueUnit: {
      type: Boolean,
      default: false,
    },
    max: {
      type: Number,
      required: false,
    },
  },
  components: { StagedChart },
  computed: {
    baseOptions() {
      const { years, showValues, showValueUnit, showTotals, max, units } = this;
      return {
        chart: {
          type: "area",
          height: 400,
          width: null,
          margin: [0, 0, 20, 0],
          spacing: [0, 0, 0, 0],
        },
        exporting: {
          enabled: false,
        },
        responsive: {
          rules: [
            {
              condition: {
                maxWidth: 420,
              },
              chartOptions: {
                chart: {
                  height: 250,
                },
              },
            },
          ],
        },
        plotOptions: {
          column: {
            pointWidth: 32,
            pointPadding: 0,
            dataLabels: {
              enabled: showValues,
              allowOverlap: true,
              format: `{y}${showValueUnit ? units : ""}`,
            },
          },
          series: {
            states: {
              hover: {
                enabled: false,
              },
            },
            animation: false,
          },
        },
        yAxis: {
          visible: true,
          max,
          ceiling: max,
          labels: {
            enabled: false,
          },
          stackLabels: showTotals
            ? {
                enabled: true,
                format: `{total:.0f}${units}`,
                textAlign: "center",
                align: "center",
                x: 14,
              }
            : {},
        },
        xAxis: {
          tickPositions: years,
          tickLength: 0,
        },
        legend: {
          enabled: false,
        },
        tooltip: {
          enabled: false,
        },
      };
    },
    years() {
      return this.chartData[this.scenario][0].data.map((row) => row[0]);
    },
    scenarios() {
      return Object.keys(this.chartData);
    },
    stages() {
      const { chartData } = this;
      return Object.keys(chartData).map((scenario) => ({
        series: [
          // area series
          ...chartData[scenario].map((series) => ({
            ...series,
            data: series.data.reduce(
              (data, row) => [
                ...data,
                [row[0] - 0.75, row[1]],
                [row[0] + 0.75, row[1]],
              ],
              []
            ),
          })),
          // column series
          ...chartData[scenario].map((series) => ({
            ...series,
            data: series.data.map((data) =>
              data[1] === 0
                ? { x: data[0], y: data[1], dataLabels: { enabled: false } }
                : data
            ),
            type: "column",
          })),
        ],
      }));
    },
  },
};
</script>

<style>
.areaColumn {
  width: 100%;
  position: relative;
  background: #fff;
}
.areaColumn .highcharts-plot-background {
  fill: #fff;
}
.areaColumn .highcharts-background {
  fill: transparent;
}
.areaColumn .highcharts-point {
  fill: inherit !important;
  stroke: inherit !important;
  stroke-width: 0px;
}
.areaColumn .highcharts-graph {
  stroke-width: 0px;
}
.areaColumn .highcharts-axis-line {
  stroke-width: 0px;
}
.areaColumn rect.highcharts-data-label-box {
  fill: transparent !important;
}
</style>
